export type Kind = "html" | "audio" | "image" | "video" | "pdf" | "text" | "archive" | "other";

export type FileKind = { kind: Kind; label: string; badge: string };

const extensionOf = (name: string) => {
  const dot = name.lastIndexOf(".");
  return dot > 0 && dot < name.length - 1 ? name.slice(dot + 1).toLowerCase() : "";
};

const ARCHIVES = new Set(["zip", "gz", "tgz", "tar", "7z", "rar", "bz2", "xz"]);
const TEXT = new Set(["txt", "md", "csv", "json", "js", "ts", "css", "xml", "yaml", "yml", "log"]);

const NAMED: Record<string, string> = {
  "text/plain": "Plain text",
  "text/markdown": "Markdown",
  "text/csv": "CSV table",
  "application/json": "JSON",
};

export const isHtml = (contentType: string | null, name: string): boolean => {
  const ext = extensionOf(name);
  return (
    contentType?.startsWith("text/html") === true ||
    contentType?.startsWith("application/xhtml") === true ||
    ext === "html" ||
    ext === "htm"
  );
};

export const kindOf = (contentType: string | null, name: string): FileKind => {
  const ext = extensionOf(name);
  const type = (contentType ?? "").split(";")[0]?.trim().toLowerCase() ?? "";
  const badge = (ext || type.split("/")[1] || "file").slice(0, 4).toUpperCase();
  const format = ext ? ext.toUpperCase() : badge;

  if (isHtml(type, name)) return { kind: "html", label: "HTML page", badge: "HTML" };
  if (type === "application/pdf" || ext === "pdf") {
    return { kind: "pdf", label: "PDF document", badge: "PDF" };
  }
  if (type.startsWith("audio/")) return { kind: "audio", label: `${format} audio`, badge };
  if (type.startsWith("image/")) return { kind: "image", label: `${format} image`, badge };
  if (type.startsWith("video/")) return { kind: "video", label: `${format} video`, badge };
  if (ARCHIVES.has(ext) || type.includes("zip") || type.includes("compressed")) {
    return { kind: "archive", label: `${format} archive`, badge };
  }
  const named = NAMED[type];
  if (named) return { kind: "text", label: named, badge };
  if (type.startsWith("text/") || TEXT.has(ext)) {
    return { kind: "text", label: `${format} text`, badge };
  }
  return { kind: "other", label: ext ? `${format} file` : "File", badge };
};
